/** The `{ error, detail }` JSON body every GhostWatch API error response uses (see `error.rs`). */
export interface ApiErrorBody {
  error: string;
  detail: string | null;
}

/**
 * Typed error thrown by every {@link GhostWatchClient} method. `status` is the HTTP
 * status code, or `0` for a request that never got a response (network failure, or a
 * response whose JSON didn't match the expected shape) — callers get one uniform
 * catchable type regardless of failure mode.
 */
export class ApiError extends Error implements ApiErrorBody {
  readonly status: number;
  readonly error: string;
  readonly detail: string | null;

  constructor(status: number, body: ApiErrorBody) {
    super(body.detail ? `${body.error}: ${body.detail}` : body.error);
    this.name = "ApiError";
    this.status = status;
    this.error = body.error;
    this.detail = body.detail;
  }

  /** True for 401 responses — callers use this to redirect to /login. */
  get isUnauthorised(): boolean {
    return this.status === 401;
  }

  /** True for 403 responses — a permission the caller's role doesn't grant. */
  get isForbidden(): boolean {
    return this.status === 403;
  }
}

export function isApiError(err: unknown): err is ApiError {
  return err instanceof ApiError;
}
